import React, { useState } from 'react';
import { 
  Modal, 
  Carousel, 
  Row, 
  Col, 
  Badge, 
  Button,
  Card,
  Tab,
  Nav,
  Form,
  InputGroup,
  Dropdown,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt,
  faBed,
  faBath,
  faRuler,
  faHome,
  faCalendarAlt,
  faCoins,
  faSwimmingPool,
  faCar,
  faPhone,
  faEnvelope,
  faHeart,
  faShare,
  faPrint,
  faLink,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faTwitter, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Tabs } from 'react-bootstrap';
import axios from 'axios';

interface Property {
  id: string;
  mlsNumber: string;
  image?: string;
  images?: string[];
  price: number;
  bedrooms: number;
  bathrooms: number;
  squareFeet?: number;
  sqft?: number;
  address: string | { street?: string; city?: string; state?: string; zip?: string };
  city: string;
  state: string;
  zipCode?: string;
  zip?: string;
  daysOnMarket?: number;
  propertyType: string;
  propertySubType?: string;
  features?: string[];
  description?: string;
  listingDate?: string;
  status?: string;
  yearBuilt?: number;
  lotSize?: number;
  garage?: number;
  pool?: boolean;
  hoaFee?: number;
  listingAgent?: {
    name?: string;
    phone?: string;
    email?: string;
    officeName?: string;
  };
  virtualTour?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface PropertyDetailsModalProps {
  property: Property | null;
  show: boolean;
  onHide: () => void;
}

const PropertyDetailsModal: React.FC<PropertyDetailsModalProps> = ({
  property,
  show,
  onHide
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isPropertySaved, setIsPropertySaved] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [shareLink, setShareLink] = useState('');

  if (!property) return null;
  
  // Handle nested property_data structure
  const data = (property as any).property_data || property;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getFullAddress = () => {
    if (typeof data.address === 'string') {
      return data.address;
    }
    return data.address?.street || property.address || 'Address Not Available';
  };

  const getCityStateZip = () => {
    if (typeof data.address === 'object' && data.address?.city) {
      return `${data.address.city}, ${data.address.state} ${data.address.zip}`;
    }
    return `${data.city || property.city}, ${data.state || property.state} ${data.zipCode || data.zip || property.zipCode || (property as any).zip_code || ''}`;
  };

  const getSquareFeet = () => {
    return data.squareFeet || data.sqft || (property as any).square_feet || 0;
  };

  const allImages = data.images?.length ? data.images : 
    (data.image ? [data.image] : []);

  // Save property handler
  const handleSaveProperty = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setToastMessage('Please login to save properties');
      setToastType('error');
      setShowToast(true);
      return;
    }

    try {
      const endpoint = isPropertySaved 
        ? `/api/mls-portal/actions/save/${data.mlsNumber || property.mlsNumber}`
        : '/api/mls-portal/actions/save';
      
      const method = isPropertySaved ? 'DELETE' : 'POST';
      
      const response = await axios({
        method,
        url: `${process.env.REACT_APP_API_URL}${endpoint}`,
        data: !isPropertySaved ? {
          mlsNumber: data.mlsNumber || property.mlsNumber,
          propertyData: data
        } : undefined,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setIsPropertySaved(!isPropertySaved);
      setToastMessage(isPropertySaved ? 'Property removed from favorites' : 'Property saved to favorites');
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error saving property:', error);
      setToastMessage('Failed to save property');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Share property handler
  const handleShareProperty = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/mls-portal/actions/share`,
        {
          mlsNumber: data.mlsNumber || property.mlsNumber,
          propertyData: data,
          recipientEmail: shareEmail,
          message: shareMessage
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      setShareLink(response.data.data.shareLink);
      setToastMessage(shareEmail ? 'Property shared via email!' : 'Share link generated!');
      setToastType('success');
      setShowToast(true);
      
      if (!shareEmail) {
        // Copy link to clipboard
        navigator.clipboard.writeText(response.data.data.shareLink);
      }
      
      setShowShareModal(false);
      setShareEmail('');
      setShareMessage('');
    } catch (error) {
      console.error('Error sharing property:', error);
      setToastMessage('Failed to share property');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Print property handler
  const handlePrintProperty = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/mls-portal/actions/print`,
        { propertyData: data },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `property-${data.mlsNumber || property.mlsNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setToastMessage('Property sheet downloaded!');
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      console.error('Error printing property:', error);
      setToastMessage('Failed to generate property sheet');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Social share handlers
  const handleSocialShare = (platform: string) => {
    const url = encodeURIComponent(shareLink || window.location.href);
    const text = encodeURIComponent(`Check out this property: ${getFullAddress()}`);
    
    const shareUrls: { [key: string]: string } = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <>
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="property-details-modal"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="d-flex align-items-center justify-content-between w-100">
          <div>
            <h4 className="mb-1">{formatPrice(data.price || property.price)}</h4>
            <p className="mb-0 text-muted">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
              {getFullAddress()}, {getCityStateZip()}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button 
              variant={isPropertySaved ? "primary" : "outline-primary"} 
              size="sm"
              onClick={handleSaveProperty}
            >
              <FontAwesomeIcon 
                icon={faHeart} 
                className={`me-1 ${isPropertySaved ? 'text-white' : ''}`}
              /> 
              {isPropertySaved ? 'Saved' : 'Save'}
            </Button>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => setShowShareModal(true)}
            >
              <FontAwesomeIcon icon={faShare} className="me-1" /> Share
            </Button>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handlePrintProperty}
            >
              <FontAwesomeIcon icon={faPrint} className="me-1" /> Print
            </Button>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-0">
        <Row>
          {/* Photo Gallery */}
          <Col lg={8}>
            {allImages.length > 0 ? (
              <Carousel 
                activeIndex={activeImageIndex}
                onSelect={setActiveImageIndex}
                className="property-carousel mb-3"
              >
                {allImages.map((image: string, index: number) => (
                  <Carousel.Item key={index}>
                    <img
                      className="d-block w-100"
                      src={image}
                      alt={`Property ${index + 1}`}
                      style={{ maxHeight: '500px', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 
                          'https://via.placeholder.com/800x500/e9ecef/6c757d?text=Image+Not+Available';
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <div className="bg-light d-flex align-items-center justify-content-center" 
                style={{ height: '400px' }}>
                <p className="text-muted">No Images Available</p>
              </div>
            )}

            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <Row className="g-2 mb-3">
                {allImages.slice(0, 6).map((image: string, index: number) => (
                  <Col xs={2} key={index}>
                    <img
                      src={image}
                      alt={`Thumb ${index + 1}`}
                      className={`img-fluid cursor-pointer ${activeImageIndex === index ? 'border border-primary' : ''}`}
                      onClick={() => setActiveImageIndex(index)}
                      style={{ height: '60px', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 
                          'https://via.placeholder.com/100x60/e9ecef/6c757d?text=No+Image';
                      }}
                    />
                  </Col>
                ))}
                {allImages.length > 6 && (
                  <Col xs={2}>
                    <div 
                      className="bg-light d-flex align-items-center justify-content-center cursor-pointer"
                      style={{ height: '60px' }}
                      onClick={() => setActiveImageIndex(6)}
                    >
                      +{allImages.length - 6} more
                    </div>
                  </Col>
                )}
              </Row>
            )}

            {/* Property Details Tabs */}
            <Tab.Container defaultActiveKey="overview">
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="overview">Overview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="features">Features</Nav.Link>
                </Nav.Item>
                {data.description && (
                  <Nav.Item>
                    <Nav.Link eventKey="description">Description</Nav.Link>
                  </Nav.Item>
                )}
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="overview">
                  <Row>
                    <Col md={6}>
                      <h6>Property Details</h6>
                      <ul className="list-unstyled">
                        <li><strong>MLS#:</strong> {data.mlsNumber || (property as any).mls_number}</li>
                        <li><strong>Type:</strong> {data.propertySubType || data.propertyType || (property as any).property_type}</li>
                        <li><strong>Year Built:</strong> {data.yearBuilt || 'N/A'}</li>
                        <li><strong>Lot Size:</strong> {data.lotSize ? `${data.lotSize.toLocaleString()} sq ft` : 'N/A'}</li>
                        <li><strong>Days on Market:</strong> {data.daysOnMarket || 'N/A'}</li>
                        <li><strong>Status:</strong> <Badge bg="success">{data.status || property.status || 'Active'}</Badge></li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <h6>Additional Information</h6>
                      <ul className="list-unstyled">
                        {data.hoaFee !== undefined && (
                          <li><strong>HOA Fee:</strong> ${data.hoaFee}/month</li>
                        )}
                        {data.garage !== undefined && (
                          <li><strong>Garage:</strong> {data.garage} car spaces</li>
                        )}
                        <li><strong>Pool:</strong> {data.pool ? 'Yes' : 'No'}</li>
                        {data.listingDate && (
                          <li><strong>Listed:</strong> {new Date(data.listingDate).toLocaleDateString()}</li>
                        )}
                      </ul>
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="features">
                  {data.features && data.features.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {data.features.map((feature: string, index: number) => (
                        <Badge key={index} bg="secondary" className="p-2">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No features listed</p>
                  )}
                </Tab.Pane>
                {data.description && (
                  <Tab.Pane eventKey="description">
                    <p>{data.description}</p>
                  </Tab.Pane>
                )}
              </Tab.Content>
            </Tab.Container>
          </Col>

          {/* Property Summary Sidebar */}
          <Col lg={4}>
            {/* Key Features */}
            <Card className="mb-3">
              <Card.Body>
                <h6 className="mb-3">Key Features</h6>
                <Row className="g-3">
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faBed} size="2x" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{data.bedrooms || property.bedrooms || 0}</strong></p>
                      <small className="text-muted">Bedrooms</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faBath} size="2x" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{data.bathrooms || property.bathrooms || 0}</strong></p>
                      <small className="text-muted">Bathrooms</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faRuler} size="2x" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{getSquareFeet().toLocaleString()}</strong></p>
                      <small className="text-muted">Sq Ft</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faCar} size="2x" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{data.garage || 0}</strong></p>
                      <small className="text-muted">Garage</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Listing Agent */}
            {data.listingAgent && (
              <Card className="mb-3">
                <Card.Body>
                  <h6 className="mb-3">Listing Information</h6>
                  <p className="mb-2">
                    <strong>{data.listingAgent.name || 'Listing Agent'}</strong>
                  </p>
                  {data.listingAgent.officeName && (
                    <p className="text-muted small mb-3">
                      {data.listingAgent.officeName}
                    </p>
                  )}
                  <p className="text-muted small mb-0">
                    Courtesy of {data.listingAgent.officeName || data.listingAgent.name || 'Listing Office'}
                  </p>
                  
                  <div className="mt-3 d-grid gap-2">
                    {data.listingAgent.phone && (
                      <Button variant="outline-primary" size="sm">
                        <FontAwesomeIcon icon={faPhone} className="me-2" />
                        Contact Agent
                      </Button>
                    )}
                    {data.listingAgent.email && (
                      <Button variant="outline-primary" size="sm">
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        Email Agent
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Virtual Tour */}
            {data.virtualTour && (
              <Card className="mb-3">
                <Card.Body>
                  <h6 className="mb-3">Virtual Tour</h6>
                  <Button 
                    variant="primary" 
                    className="w-100"
                    onClick={() => window.open(data.virtualTour, '_blank')}
                  >
                    <FontAwesomeIcon icon={faHome} className="me-2" />
                    View Virtual Tour
                  </Button>
                </Card.Body>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <Card.Body>
                <h6 className="mb-3">Interested?</h6>
                <div className="d-grid gap-2">
                  <Button variant="primary">
                    Schedule a Tour
                  </Button>
                  <Button variant="outline-primary">
                    Request More Info
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
    
    {/* Share Modal */}
    <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Share Property</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="email" className="mb-3">
          <Tab eventKey="email" title="Email">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Recipient Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email address"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Message (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Add a personal message..."
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                />
              </Form.Group>
              <Button variant="primary" onClick={handleShareProperty}>
                Send Email
              </Button>
            </Form>
          </Tab>
          
          <Tab eventKey="social" title="Social Media">
            <div className="d-flex gap-3 justify-content-center">
              <Button
                variant="outline-primary"
                onClick={() => handleSocialShare('facebook')}
              >
                <FontAwesomeIcon icon={faFacebook} size="2x" />
                <div className="small mt-1">Facebook</div>
              </Button>
              <Button
                variant="outline-info"
                onClick={() => handleSocialShare('twitter')}
              >
                <FontAwesomeIcon icon={faTwitter} size="2x" />
                <div className="small mt-1">Twitter</div>
              </Button>
              <Button
                variant="outline-success"
                onClick={() => handleSocialShare('whatsapp')}
              >
                <FontAwesomeIcon icon={faWhatsapp} size="2x" />
                <div className="small mt-1">WhatsApp</div>
              </Button>
            </div>
          </Tab>
          
          <Tab eventKey="link" title="Copy Link">
            <InputGroup className="mb-3">
              <Form.Control
                type="text"
                value={shareLink || `${window.location.origin}/mls-portal/property/${data.mlsNumber}`}
                readOnly
              />
              <Button
                variant="outline-secondary"
                onClick={() => {
                  navigator.clipboard.writeText(shareLink || `${window.location.origin}/mls-portal/property/${data.mlsNumber}`);
                  setToastMessage('Link copied to clipboard!');
                  setToastType('success');
                  setShowToast(true);
                }}
              >
                <FontAwesomeIcon icon={faLink} className="me-1" />
                Copy
              </Button>
            </InputGroup>
            {!shareLink && (
              <Button variant="primary" onClick={() => handleShareProperty()}>
                Generate Share Link
              </Button>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
    
    {/* Toast Notifications */}
    <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        bg={toastType === 'success' ? 'success' : 'danger'}
      >
        <Toast.Body className="text-white">
          {toastType === 'success' && <FontAwesomeIcon icon={faCheck} className="me-2" />}
          {toastMessage}
        </Toast.Body>
      </Toast>
    </ToastContainer>
    </>
  );
};

export default PropertyDetailsModal;